import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { LayerBroadCastChannelData } from '@connect-services/client/models/broadcast-channel-data.model';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { DepositWithStandard } from '@connect-services/client/services/synoptic/export-synoptic-cartouche.service';
import {
    Deposit,
    DepositsService,
    Synoptic,
    SynopticControllerGetOneRequestParams,
    SynopticService,
    SynopticZone,
} from '@connect-services/src/sdk/api';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export interface SelectedSynoptic extends Synoptic {
    znUuid: string;
}

@Injectable({
    providedIn: 'root',
})
export class SelectSynopticService {
    public readonly depositList: WritableSignal<DepositWithStandard[]> = signal([]);
    public readonly hasSynopticAvailable = signal(true);
    public readonly isEditMode = signal<boolean>(false);
    public readonly isLoading = signal(true);
    public readonly isSynopticDisplayed: WritableSignal<boolean> = signal(false);
    public readonly isSynopticGenerating: WritableSignal<boolean> = signal(true);
    public readonly selectedDeposit = signal<DepositWithStandard | null>(null);
    public readonly selectedSynoptic: WritableSignal<SelectedSynoptic | null> = signal(null);
    public readonly selectedSynopticZone = signal<SynopticZone | null>(null);
    public readonly synopticZones: WritableSignal<SynopticZone[]> = signal([]);

    private readonly depositService = inject(DepositsService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly snackBarService = inject(SnackBarService);
    private readonly synopticService = inject(SynopticService);

    public async changeSynopticIfNeeded(eventData: LayerBroadCastChannelData): Promise<void> {
        let hasSynopticChanged = false;

        if (!this.depositList().length) {
            await this.loadSynopticData();
        }

        if (eventData.deposit !== this.selectedDeposit()?.id) {
            const entityDeposit = this.depositList().find(
                (deposit: DepositWithStandard) => deposit.id === eventData.deposit,
            );

            if (!entityDeposit) {
                return;
            }

            hasSynopticChanged = true;
            await this.selectSynopticZone(entityDeposit);
        }

        if (eventData.znUuidAttributeValue !== this.selectedSynoptic()?.znUuid) {
            const synopticZone = this.synopticZones().find(
                (zone: SynopticZone) => zone.zn_uuid === eventData.znUuidAttributeValue,
            );

            if (!synopticZone) {
                return;
            }

            hasSynopticChanged = true;
            await this.selectSynoptic(synopticZone);
        }

        if (hasSynopticChanged) {
            this.snackBarService.openSuccessSnackBar('synopticChanged');
        }
    }

    public clearSelections(): void {
        this.resetSynopticSelection();
        this.selectedDeposit.set(null);
        this.synopticZones.set([]);
    }

    public async loadSynopticData(): Promise<void> {
        const project = this.projectBloc.value();
        if (!project) {
            return;
        }

        try {
            const deposits: Deposit[] = await firstValueFrom(
                this.depositService.depositControllerGetDepositWithSynoptic({ projectId: project.id }),
            );

            this.depositList.set(
                deposits.sort((a: Deposit, b: Deposit) => a.name.localeCompare(b.name)) as DepositWithStandard[],
            );

            if (!this.depositList().length) {
                this.hasSynopticAvailable.set(false);

                return;
            }

            this.hasSynopticAvailable.set(true);

            if (!this.selectedDeposit() && this.depositList().length === 1) {
                await this.selectSynopticZone(this.depositList()[0]);
            }
        } catch {
            this.snackBarService.openErrorSnackBar('errorGET');
            this.hasSynopticAvailable.set(false);
        } finally {
            this.isLoading.set(false);
        }
    }

    public resetAll(): void {
        this.clearSelections();
    }

    public resetSynopticSelection(): void {
        this.selectedSynopticZone.set(null);
        this.selectedSynoptic.set(null);
        this.isSynopticDisplayed.set(false);
    }

    public async selectSynoptic(synopticZone: SynopticZone): Promise<void> {
        this.isSynopticGenerating.set(true);
        try {
            this.selectedSynopticZone.set(synopticZone);

            const selectedDeposit = this.selectedDeposit();
            if (!selectedDeposit) {
                return;
            }

            const params: SynopticControllerGetOneRequestParams = {
                depositId: selectedDeposit.id,
                tableName: synopticZone.table_name,
            };

            if (this.projectEditModeService.isEditModeActive()) {
                const editSession = this.projectEditModeService.getEditSession();

                params.editSessionId = editSession?.id;
                params.znUuid = synopticZone.zn_uuid;
            }

            const synoptic = await firstValueFrom(this.synopticService.synopticControllerGetOne(params));

            this.selectedSynoptic.set({
                ...synoptic,
                znUuid: synopticZone.zn_uuid,
            });
            this.isSynopticDisplayed.set(true);
        } catch {
            this.snackBarService.openErrorSnackBar('errorGET');
            this.isSynopticGenerating.set(false);
        } finally {
            this.isLoading.set(false);
        }
    }

    public async selectSynopticZone(deposit: DepositWithStandard): Promise<void> {
        try {
            this.selectedSynopticZone.set(null);
            this.synopticZones.set([]);
            this.selectedDeposit.set(deposit);

            const synopticZones = await firstValueFrom(
                this.synopticService.synopticControllerGetZones({ depositId: deposit.id }),
            );

            this.synopticZones.set(synopticZones);

            if (this.synopticZones().length === 1) {
                await this.selectSynoptic(this.synopticZones()[0]);

                return;
            }

            this.selectedSynoptic.set(null);
        } catch {
            this.snackBarService.openErrorSnackBar('errorGET');
        } finally {
            this.isLoading.set(false);
        }
    }
}
